import MailOutlineIcon from "@mui/icons-material/MailOutline";
import classNames from "classnames";
import saveAs from "file-saver";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadEmailTemplate } from "../../api/useDownloadEmailTemplate";
import { useDownloadDocument, usePrintDoc } from "../../api/usePrintDoc";
import SellSheetModal from "../../components/SellSheetModal/SellSheetModal";
import { useClientPrintConfiguration } from "../../data/global/useProductConfiguration";
import { HCPSpecificMessage, Message, PrintDoc } from "../../generate";
import EmailTemplateButton from "./EmailTemplateButton";

interface Props {
  client: string;
  hcp: HCPSpecificMessage;
  sellSheet: Message;
}

const EmailSellSheetModal: React.FC<Props> = ({ client, hcp, sellSheet }) => {
  const [t] = useTranslation();
  const config = useClientPrintConfiguration(client);
  const [includePi, setIncludePi] = useState(config.defaultIsPIEmail);

  const printDoc: PrintDoc = {
    client,
    baseDocName: sellSheet.messageName,
    prescID: hcp.prescID,
    includePI: includePi,
    isResource: false,
    isPrint: false,
  };

  const { isLoading: isDownloading, mutateAsync: downloadEmailTemplate } = useDownloadEmailTemplate(printDoc, hcp);

  const documentQuery = usePrintDoc({ printDoc });

  const { isLoading: isDownloadingPdf, mutateAsync: downloadDocument } = useDownloadDocument();

  const buttonClasses = classNames("btn btn-primary w-full", {
    "opacity-50 cursor-disabled": isDownloading,
  });

  const secondaryButtonClasses = classNames("btn w-full text-white border-0", {
    "opacity-50 cursor-disabled": documentQuery.isLoading || isDownloadingPdf,
  });

  const secondaryButtonStyle = {
    backgroundColor: "rgb(0, 72, 119)",
    borderColor: "rgb(0, 72, 119)",
  };

  const staticButtonClasses = "btn w-full text-white border-0";

  const handlePrintPDFClick = async () => {
    const url = documentQuery.data && documentQuery.data.downloadURL ? documentQuery.data.downloadURL : "";
    if (url) {
      await downloadDocument({ printDoc, hcp, url });
    }
  };

  const downloadEmailLanguageDocument = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const documentUrl = `/assets/documents/PAXLOVID Email From iOS Instructions.pdf`;
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      saveAs(blob, "PAXLOVID Email From iOS Instructions.pdf");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Download failed:", error);
    }
  };

  const showEmailIosButtons = config?.showEmailIosButtons === true;

  return (
    <SellSheetModal
      client={client}
      hcp={hcp}
      sellSheet={sellSheet}
      showIsPI={config.useIsPIForEmailMessages}
      includePi={includePi}
      onIncludePiChange={(value) => setIncludePi(value)}
      isPrint={false}
    >
      <div className="flex flex-col space-y-3">
        {config.emailTemplateButtons?.length ? (
          config.emailTemplateButtons.map((button) => (
            <EmailTemplateButton
              key={button.type}
              client={client}
              hcp={hcp}
              baseDocName={sellSheet.messageName ?? undefined}
              prescID={hcp.prescID ?? undefined}
              includePI={includePi}
              templateType={button.type}
              labelKey={button.labelKey}
              includeAttachment={button.includeAttachment}
            />
          ))
        ) : (
          <button disabled={isDownloading} className={buttonClasses} onClick={() => downloadEmailTemplate()}>
            <MailOutlineIcon />
            <span>{isDownloading ? t("loading") : t("specificMessages.downloadEmailTemplate")}</span>
          </button>
        )}

        {showEmailIosButtons && (
          <div className="flex flex-col space-y-3">
            <button
              disabled={documentQuery.isLoading || isDownloadingPdf}
              className={secondaryButtonClasses}
              style={secondaryButtonStyle}
              onClick={handlePrintPDFClick}
            >
              <MailOutlineIcon />
              <span>
                {isDownloading || isDownloadingPdf ? t("loading") : t("specificMessages.downloadPDFAndCCMessage")}
              </span>
            </button>

            <button
              disabled={false}
              className={staticButtonClasses}
              style={secondaryButtonStyle}
              onClick={downloadEmailLanguageDocument}
            >
              <MailOutlineIcon />
              <span>{t("specificMessages.downloadPDFInstructions")}</span>
            </button>
          </div>
        )}
      </div>
    </SellSheetModal>
  );
};

export default EmailSellSheetModal;
